/* eslint-disable @typescript-eslint/no-explicit-any */
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { useCreateMatterMutation, useGetFirmQuery } from '../../services/api';
import {
  MatterStatus,
  MatterType,
} from '../../services/types/client-intake-types';
import { selectUser } from '../../state/selectors/root-selectors';
import { extractAttorneyId } from '../../utils/attorney-helpers';
import { sanitizePhoneNumber } from '../../utils/validation';
import { useFirmServicesForForm } from '../services/hooks';
import { AdditionalDetailsSection } from './AdditionalDetailsSection';
import { BasicsSection } from './BasicsSection';
import { BillingSection } from './BillingSection';
import { CaseManagerSection } from './CaseManagerSection';
import { ClientContactSection } from './ClientContactSection';
import { CMSIntegrationSection } from './CMSIntegrationSection';
import { DuplicateSuggestions } from './DuplicateSuggestions';
import { FirmSection } from './FirmSection';
import { FormActions } from './FormActions';
import { createMatterSchema } from './FormSchema';
import { CreateMatterFormValues } from './types';
import { useDuplicateDetection } from './useDuplicateDetection';

interface CreateMatterFormProps {
  onSuccess?: (matterId?: string) => void;
  onCancel?: () => void;
}

export const CreateMatterForm: React.FC<CreateMatterFormProps> = ({
  onSuccess,
  onCancel,
}) => {
  const [createMatter, { isLoading }] = useCreateMatterMutation();
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const [selectedFirm, setSelectedFirm] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [pendingFormData, setPendingFormData] =
    useState<CreateMatterFormValues | null>(null);

  // Fetch full firm data to get feature flags
  const { data: firmData } = useGetFirmQuery(selectedFirm?.id ?? '', {
    skip: !selectedFirm?.id,
  });

  // Check if matter workflow overrides feature is enabled for this firm
  // Firms must have this enabled to create matters
  const isMatterWorkflowsEnabled =
    firmData?.feature_flags?.matter_workflow_overrides === 'true';

  // Fetch firm services to get workflow defaults (only when feature flag is ON)
  const { services: firmServices } = useFirmServicesForForm(
    isMatterWorkflowsEnabled ? (selectedFirm?.id ?? null) : null,
  );

  // Find the client_check_in workflow enabled status from firm services
  const clientCheckInEnabled = useMemo(() => {
    if (!isMatterWorkflowsEnabled || !firmServices) return null;

    const clientCommunicationService = firmServices.find(
      (service) => service.service_name === 'client_communication',
    );

    const clientCheckInWorkflow = clientCommunicationService?.workflows?.find(
      (workflow) => workflow.workflow_code === 'client_check_in',
    );

    return clientCheckInWorkflow?.is_enabled ?? false;
  }, [isMatterWorkflowsEnabled, firmServices]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<CreateMatterFormValues>({
    resolver: zodResolver<CreateMatterFormValues>(createMatterSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      matter_type: MatterType.MOTOR_VEHICLE_ACCIDENT,
      status: undefined,
      phoneNumber: '',
      firm: { id: '', name: '' },
      contact_client_directly: true,
      external_cms_url: null,
      external_drive_url: null,
      cms_email: null,
      case_id: null,
      simplified_billing_code: null,
      case_manager_id: null,
    },
  });

  // Set contact_client_directly default based on firm's CLIENT_CHECK_IN workflow
  useEffect(() => {
    if (clientCheckInEnabled !== null) {
      setValue('contact_client_directly', clientCheckInEnabled);
    }
  }, [clientCheckInEnabled, setValue]);

  const matterName = watch('name') || '';

  // Watch billing code and status fields
  const selectedBillingCode = watch('simplified_billing_code');
  const isLitigationOnly = watch('status') === MatterStatus.LITIGATION;

  // Check for duplicates
  const { duplicates } = useDuplicateDetection(matterName);
  const hasDuplicates = duplicates.length > 0;

  const onSubmit = async (data: CreateMatterFormValues) => {
    // If there are duplicates, show confirmation dialog first
    if (hasDuplicates) {
      setPendingFormData(data);
      setShowDuplicateDialog(true);
      return;
    }

    // No duplicates, proceed with creation
    await createMatterInternal(data);
  };

  const createMatterInternal = async (data: CreateMatterFormValues) => {
    if (!user) {
      toast.error('User must be logged in to create a matter');
      return;
    }

    try {
      // Services are auto-copied from firm defaults by the backend
      const result = await createMatter({
        firm: data.firm.id,
        creator: user.id,
        name: data.name,
        matter_type: data.matter_type,
        type: data.matter_type,
        date_of_loss: data.date_of_loss,
        statute_of_limitations_date: data.statute_of_limitations_date,
        city: data.city,
        state: data.state,
        contact_client_directly: data.contact_client_directly,
        status: data.status || MatterStatus.INITIAL_REVIEW,
        simplified_billing_code: data.simplified_billing_code,
        external_cms_url: data.external_cms_url || null,
        external_drive_url: data.external_drive_url || null,
        cms_email: data.cms_email || null,
        case_id: data.case_id || null,
        ...(data.representing_attorney
          ? {
              representing_attorney: extractAttorneyId(
                data.representing_attorney,
              ),
            }
          : {}),
        clients: [
          {
            phone_numbers: data.phoneNumber
              ? [{ phone_number: sanitizePhoneNumber(data.phoneNumber) }]
              : [],
          },
        ],
        // Include case manager if selected, along with auto_task_assignment setting
        ...(data.case_manager_id
          ? {
              case_manager_id: data.case_manager_id,
              auto_task_assignment: data.auto_task_assignment ?? true,
            }
          : {}),
      }).unwrap();

      toast.success(`Matter "${result.matter.name}" created successfully`);
      reset();

      if (onSuccess) {
        onSuccess(result.matter.id);
      } else {
        navigate(`/matters/${result.matter.id}`);
      }
    } catch (error) {
      toast.error(`Failed to create matter`);
    }
  };

  const handleCancel = () => {
    reset();
    if (onCancel) {
      onCancel();
    }
  };

  const handleConfirmCreate = async () => {
    setShowDuplicateDialog(false);
    if (pendingFormData) {
      await createMatterInternal(pendingFormData);
      setPendingFormData(null);
    }
  };

  const handleCancelCreate = () => {
    setShowDuplicateDialog(false);
    setPendingFormData(null);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="mx-auto max-w-4xl">
        <h1 className="text-2xl/8 font-semibold text-zinc-950 sm:text-xl/8 dark:text-white">
          Create Matter
        </h1>
        <hr
          role="presentation"
          className="my-10 mt-6 w-full border-t border-zinc-950/10 dark:border-white/10"
        />

        {/* Firm Scope of Work Configuration Required Banner */}
        {selectedFirm && !isMatterWorkflowsEnabled && (
          <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-3 dark:border-red-700 dark:bg-red-950/30">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Scope of Work Configuration Required
                </h3>
                <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                  The scope of work must be configured for this firm before
                  creating matters.
                </p>
                <Link
                  to={`/firms/${selectedFirm.id}/services`}
                  className="mt-2 inline-flex items-center text-sm font-medium text-red-800 underline hover:text-red-900 dark:text-red-200 dark:hover:text-red-100"
                >
                  Configure Scope of Work →
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Firm */}
        <FirmSection
          control={control}
          errors={errors}
          selectedFirm={selectedFirm}
          setSelectedFirm={setSelectedFirm}
          setValue={setValue}
          isLoading={isLoading}
        />

        {/* Basics */}
        <BasicsSection
          control={control as any}
          errors={errors}
          isLoading={isLoading}
          matterName={matterName}
        />

        {/* Client Contact */}
        <ClientContactSection
          control={control as any}
          errors={errors}
          isLoading={isLoading}
        />

        {/* Case Manager - only shows if firm has case managers */}
        <CaseManagerSection
          control={control}
          firmId={selectedFirm?.id}
          isLoading={isLoading}
          setValue={setValue}
        />

        {/* Billing */}
        <BillingSection
          selectedBillingCode={selectedBillingCode}
          setValue={setValue}
          isLoading={isLoading}
          firmId={selectedFirm?.id}
        />

        <CMSIntegrationSection control={control} />

        {/* Additional Details Accordion */}
        <AdditionalDetailsSection
          control={control as any}
          errors={errors}
          isLoading={isLoading}
          isLitigationOnly={isLitigationOnly}
          setValue={setValue}
        />

        {/* Form Actions */}
        <FormActions
          handleCancel={handleCancel}
          isLoading={isLoading}
          isValid={isValid}
          disabled={selectedFirm !== null && !isMatterWorkflowsEnabled}
          disabledReason="Scope of work must be configured for this firm"
        />
      </form>

      {/* Duplicate Confirmation Dialog */}
      <AlertDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Potential Duplicate Detected</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  We found {duplicates.length} similar{' '}
                  {duplicates.length === 1
                    ? 'matter or intake'
                    : 'matters or intakes'}{' '}
                  that may match what you&apos;re creating:
                </p>
                <DuplicateSuggestions suggestions={duplicates} />
                <p className="text-sm">
                  Are you sure you want to create a new matter?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelCreate}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCreate}>
              Create Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
